import { useMemo, useState } from "react";
import { balanceSheet, incomeStatement, negate } from "@core/index";
import { useAppStore } from "@state/store";
import { useLedger } from "@ui/useLedger";
import { MoneyText, LoadingSkeleton, EmptyState, ErrorState } from "@ui/components";
import { Link } from "react-router-dom";

type Tab = "pnl" | "bs";

export function Statements() {
  const { status, error, accounts, entries, base, entityId, scopeName, isEmpty } = useLedger();
  const load = useAppStore((s) => s.load);
  const [tab, setTab] = useState<Tab>("pnl");
  const [detailed, setDetailed] = useState(false);

  const pnl = useMemo(
    () => incomeStatement(entries, accounts, base, entityId),
    [entries, accounts, base, entityId],
  );
  const bs = useMemo(
    () => balanceSheet(entries, accounts, base, entityId),
    [entries, accounts, base, entityId],
  );

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Statements</h1>
          <div className="sub">
            P&amp;L and balance sheet derived live from the ledger. {scopeName}, in {base}.
          </div>
        </div>
        <div className="actions">
          {tab === "pnl" ? (
            <div className="segmented" role="group" aria-label="Detail level">
              <button type="button" aria-pressed={!detailed} onClick={() => setDetailed(false)}>
                Summary
              </button>
              <button type="button" aria-pressed={detailed} onClick={() => setDetailed(true)}>
                Detailed
              </button>
            </div>
          ) : null}
          <div className="segmented" role="group" aria-label="Statement">
            <button type="button" aria-pressed={tab === "pnl"} onClick={() => setTab("pnl")}>
              P&amp;L
            </button>
            <button type="button" aria-pressed={tab === "bs"} onClick={() => setTab("bs")}>
              Balance sheet
            </button>
          </div>
        </div>
      </div>

      {status === "loading" ? (
        <LoadingSkeleton rows={5} />
      ) : status === "error" ? (
        <ErrorState
          title="Couldn’t derive statements"
          message={error ?? "The ledger store didn’t respond."}
          onRetry={load}
        />
      ) : isEmpty ? (
        <EmptyState
          glyph="▧"
          title="No statements yet"
          message="Statements are derived from posted entries. Post or import transactions and they appear here instantly."
        >
          <Link className="btn primary" to="/journal">
            Post an entry
          </Link>
        </EmptyState>
      ) : tab === "pnl" ? (
        <div className="card" style={{ maxWidth: 560 }}>
          <div className="card-head">
            <h2>Profit &amp; Loss</h2>
            <span className="top-spacer" />
            <span className="badge neutral">FY 2026 · {scopeName}</span>
          </div>
          <div className="card-body tight">
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  <tr>
                    <td>Revenue</td>
                    <td className="num">
                      <MoneyText value={pnl.revenue} />
                    </td>
                  </tr>
                  <tr>
                    <td>Cost of sales</td>
                    <td className="num">
                      <MoneyText value={negate(pnl.costOfSales)} colorSign />
                    </td>
                  </tr>
                  {detailed
                    ? pnl.costOfSalesLines.map((l) => (
                        <tr key={l.code} className="detail-row">
                          <td className="detail-label">
                            <span className="acctcode">{l.code}</span> {l.label}
                          </td>
                          <td className="num">
                            <MoneyText value={negate(l.amount)} colorSign />
                          </td>
                        </tr>
                      ))
                    : null}
                  <tr>
                    <td>
                      <strong>Gross profit</strong>
                    </td>
                    <td className="num">
                      <strong>
                        <MoneyText value={pnl.grossProfit} />
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td>Operating expenses</td>
                    <td className="num">
                      <MoneyText value={negate(pnl.operatingExpenses)} colorSign />
                    </td>
                  </tr>
                  {detailed
                    ? pnl.operatingExpenseLines.map((l) => (
                        <tr key={l.code} className="detail-row">
                          <td className="detail-label">
                            <span className="acctcode">{l.code}</span> {l.label}
                          </td>
                          <td className="num">
                            <MoneyText value={negate(l.amount)} colorSign />
                          </td>
                        </tr>
                      ))
                    : null}
                  <tr>
                    <td>FX gain / (loss)</td>
                    <td className="num">
                      <MoneyText value={pnl.fxResult} colorSign />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Profit before tax</strong>
                    </td>
                    <td className="num">
                      <strong>
                        <MoneyText value={pnl.profitBeforeTax} />
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td>Corporate tax (9%)</td>
                    <td className="num">
                      <MoneyText value={negate(pnl.corporateTax)} colorSign />
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td>Net profit</td>
                    <td className="num">
                      <MoneyText value={pnl.netProfit} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="card" style={{ maxWidth: 560 }}>
          <div className="card-head">
            <h2>Balance Sheet</h2>
            <span className="top-spacer" />
            <span className={`badge ${bs.balances ? "good" : "bad"}`}>
              <span className="dot" /> A = L + E
            </span>
          </div>
          <div className="card-body tight">
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  <tr>
                    <td className="muted tiny" colSpan={2}>
                      ASSETS
                    </td>
                  </tr>
                  {bs.assets.map((l) => (
                    <tr key={l.code}>
                      <td>{l.label}</td>
                      <td className="num">
                        <MoneyText value={l.amount} colorSign />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <strong>Total assets</strong>
                    </td>
                    <td className="num">
                      <strong>
                        <MoneyText value={bs.totalAssets} />
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td className="muted tiny" colSpan={2}>
                      LIABILITIES &amp; EQUITY
                    </td>
                  </tr>
                  {bs.liabilities.map((l) => (
                    <tr key={l.code}>
                      <td>{l.label}</td>
                      <td className="num">
                        <MoneyText value={l.amount} colorSign />
                      </td>
                    </tr>
                  ))}
                  {bs.equity.map((l) => (
                    <tr key={l.code}>
                      <td>{l.label}</td>
                      <td className="num">
                        <MoneyText value={l.amount} colorSign />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total L + E</td>
                    <td className="num">
                      <MoneyText value={bs.totalLiabilitiesEquity} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
